
import React, { useEffect } from 'react';
import { Crown, AlertTriangle, Clock, Zap, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PRICING } from '@/constants/pricing';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAnalytics } from '@/hooks/useAnalytics';

interface DailyLimitReachedCardProps {
  processedFiles: number;
  freeLimit: number;
  isAuthenticated?: boolean;
  className?: string;
}

const DailyLimitReachedCard: React.FC<DailyLimitReachedCardProps> = ({
  processedFiles,
  freeLimit,
  isAuthenticated = true,
  className = ""
}) => {
  const { createCheckout } = useSubscription();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('reach_limit', { processedFiles, freeLimit });
  }, [trackEvent, processedFiles, freeLimit]);

  return (
    <Card className={`border-2 border-gradient-to-r from-purple-500 to-blue-600 bg-gradient-to-br from-purple-50 to-blue-50 shadow-xl transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl animate-fade-in ${className}`}>
      <CardHeader className="text-center pb-4">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-full p-4 transform transition-all duration-300 hover:scale-110 animate-pulse">
            <Crown className="h-12 w-12 text-white" />
          </div>
        </div>
        <CardTitle className="text-3xl font-bold text-gray-800 mb-2 transform transition-all duration-300 hover:scale-105">
          ¡Desbloquea Premium!
        </CardTitle>
        <CardDescription className="text-gray-600 text-lg transition-opacity duration-300 hover:opacity-80">
          Has alcanzado tu límite diario de {freeLimit} compresiones gratuitas
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Current Usage Status with enhanced animation */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 animate-bounce" />
            <div>
              <h4 className="font-semibold text-red-800">Límite Diario Alcanzado</h4>
              <p className="text-red-700 text-sm">
                {processedFiles}/{freeLimit} imágenes procesadas hoy
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
            <Clock className="w-4 h-4 animate-spin" />
            El límite se resetea a medianoche
          </div>
        </div>

        {/* Free registration upsell for anonymous users */}
        {!isAuthenticated && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-lg p-4 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-md">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-emerald-100 rounded-full p-2">
                <UserPlus className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-emerald-800">¡Opción gratuita!</h4>
                <p className="text-emerald-700 text-sm">
                  Regístrate gratis y obtén 7 compresiones adicionales hoy
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Crear cuenta gratis
            </Button>
          </div>
        )}

        {/* Premium Benefits Grid with staggered animations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: "Compresión Ilimitada", description: "Sin límites diarios" },
            { title: "Resolución 4K", description: "Hasta 4096x2160px" },
            { title: "Archivos Grandes", description: "Hasta 50MB por imagen" },
            { title: "Sin Anuncios", description: "Interfaz limpia" }
          ].map((benefit, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-purple-200 transform transition-all duration-300 hover:scale-105 hover:shadow-md hover:border-purple-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="bg-purple-100 rounded-full p-2 transition-all duration-300 hover:bg-purple-200">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 transition-colors duration-300 hover:text-purple-600">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Upgrade Buttons with enhanced hover effects */}
        <div className="space-y-3 pt-4">
          <Button 
            onClick={() => createCheckout('monthly')}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-lg py-3 transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
            size="lg"
          >
            <Crown className="w-5 h-5 mr-2 animate-pulse" />
            {PRICING.MONTHLY.label}
          </Button>
          
          <Button 
            onClick={() => createCheckout('annual')}
            variant="outline"
            className="w-full border-2 border-purple-300 hover:bg-purple-50 text-purple-700 hover:text-purple-800 transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
            size="lg"
          >
            <Crown className="w-5 h-5 mr-2" />
            {PRICING.ANNUAL.label}
          </Button>
        </div>

        <div className="text-center pt-2">
          <p className="text-sm text-gray-500 animate-pulse">
            ✨ Procesamiento instantáneo • Sin límites • Resolución 4K • Calidad superior
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyLimitReachedCard;
